import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * A bay of jars framed by two full-height posts, one jar pose per side of the
 * question:
 *
 *   post |  bar (3 wide, lying along x)   | post
 *   post | stand | disc | stand           | post
 *   post | stand | disc | stand           | post
 *   post | stand | disc | stand           | post
 *   post |  bar (3 wide, lying along x)   | post
 *
 * The three poses are the three turns JamJar can be given, and each lands on a
 * different collider (see JamJar.createColliderDesc):
 *
 * - the bars are `width: 3, height: 1`, which is the jar turned a quarter turn
 *   about z — its axis lies along x, so it stays a cuboid;
 * - the standing jars are `width: 1, height: 3`, untouched, also a cuboid;
 * - the discs are `lying: true`, the quarter turn about x, which points the jar
 *   at the camera and is the one pose that gets the round twelve-sided prism.
 *
 * So this level carries a whole load path — bar on stands and discs, stands and
 * discs on a bar — across all three colliders at once, which is exactly the
 * combination the sandwich wall never exercised.
 */

const CUBE = 1 as LevelPieceType;
const STONE = 2 as LevelPieceType;
const JAR = 3 as LevelPieceType;

const COLS = 5;

// Top slab, three middle rows, the lying jar, the cube it rests on.
const MIDDLE_ROWS = 3;
const ROWS = MIDDLE_ROWS + 3;

/** Columns holding a single full-height post instead of jars. */
const LEFT_POST = 0;
const RIGHT_POST = COLS - 1;

/** The jar bay sits between the posts, three columns wide. */
const BAY_X = LEFT_POST + 1;
const BAY_WIDTH = RIGHT_POST - BAY_X;

/** Depth layers. Two, so the wall reads as a wall and not as a single slice. */
const LAYERS = 2;

/**
 * The tallest authored block model is 1x5 (see PhysicsCube.AUTHORED_MODELS), so
 * a post taller than that is built from as few pieces as will fit. A single
 * 1x6 post spawns with a collider the art never matches, and the whole wall
 * blows apart on the first step — 20 pieces down to 2 in half a second.
 */
const MAX_BLOCK_HEIGHT = 5;

function putPost( put: ( piece: Omit<LevelPiece, "id"> ) => void, col: number ): void
{
  for ( let y = 0; y < ROWS; y += MAX_BLOCK_HEIGHT )
    put( { type: CUBE, x: col, y, width: 1, height: Math.min( MAX_BLOCK_HEIGHT, ROWS - y ), variant: 0 } );
}

function makeLayer( first_id: number ): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( COLS ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = first_id;

  const put = ( piece: Omit<LevelPiece, "id"> ) =>
  {
    for ( let row = piece.y; row < piece.y + piece.height; row++ )
      for ( let col = piece.x; col < piece.x + piece.width; col++ )
        grid[row][col] = id;

    pieces.push( { id, ...piece } );
    id++;
  };

  putPost( put, LEFT_POST );

  // Row 0 is the top of the wall, so the wall reads downwards from here: the
  // stone lid, the three middle rows, the lying jar, and the cube it sits on.
  //
  // The lid is stone, not a jar: a jar bar is round, so it only ever touches
  // what is under it along a line, and the bay it was meant to hold slid out
  // from under it. A stone slab lies flat across all three columns at once and
  // its weight is what keeps the jars seated.
  put( { type: STONE, x: BAY_X, y: 0, width: BAY_WIDTH, height: 1 } );

  put( { type: JAR, x: BAY_X, y: 1, width: 1, height: MIDDLE_ROWS, variant: 0 } );

  for ( let row = 1; row <= MIDDLE_ROWS; row++ )
    put( { type: JAR, x: BAY_X + 1, y: row, width: 1, height: 1, variant: 0, lying: true } );

  put( { type: JAR, x: BAY_X + 2, y: 1, width: 1, height: MIDDLE_ROWS, variant: 0 } );

  // The bay stands on a jar bar rather than on stone, and the bar in turn
  // stands on a cube: a round bar laid straight on the table rolls, so the cube
  // is what gives it a flat seat to sit in.
  put( { type: JAR, x: BAY_X, y: ROWS - 2, width: BAY_WIDTH, height: 1, variant: 0 } );

  put( { type: CUBE, x: BAY_X, y: ROWS - 1, width: BAY_WIDTH, height: 1, variant: 0 } );

  putPost( put, RIGHT_POST );

  return { grid, pieces };
}

/**
 * Ids have to stay unique across the whole level, so each layer starts where
 * the previous one stopped. Counted from the layer that was actually built
 * rather than from a hand-kept total — getting that total wrong hands two
 * layers the same ids, and the level collapses in a way that looks like
 * physics.
 */
function makeLayers( count: number ): LevelLayer[]
{
  const layers: LevelLayer[] = [];
  let next_id = 1;

  for ( let i = 0; i < count; i++ )
  {
    const layer = makeLayer( next_id );
    next_id += layer.pieces.length;
    layers.push( layer );
  }

  return layers;
}

const JAR_BAY_LEVEL: LevelConfig = {
  level: 90312,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  layers: makeLayers( LAYERS ),
};

export class JarBayShowcase
{
  public static readonly LEVEL = JAR_BAY_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? JAR_BAY_LEVEL : null;
  }
}
